import { Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useAdminNotifications } from "../../hooks/useAdminNotifications";
import { cn } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { formatAdminNotification } from "../../shared/admin-notification-format";

export function Notifications() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } =
    useAdminNotifications();
  const navigate = useNavigate();

  const handleNotificationClick = (notification: { id: number; linkUrl?: string; isRead: boolean }) => {
    if (!notification.isRead) {
      void markAsRead(notification.id);
    }
    if (notification.linkUrl) {
      navigate(notification.linkUrl);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative text-white hover:text-white">
          <Bell className="h-4 w-4 text-white" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1 text-[10px] font-semibold leading-none text-black ring-2 ring-background">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-80" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">Уведомления</p>
            <p className="text-xs leading-none text-muted-foreground">
              {unreadCount > 0
                ? `Всего ${String(unreadCount)} непрочитанных`
                : "Нет новых уведомлений"}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ScrollArea className="h-[300px]">
            {!notifications || notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-muted-foreground">
                <Bell className="h-8 w-8 mb-2 opacity-20" />
                <p className="text-xs">Список уведомлений пуст</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const visual = formatAdminNotification(notification.type);
                const Icon = visual.icon;
                return (
                <DropdownMenuItem
                  key={notification.id}
                  className="cursor-pointer flex flex-col items-start p-4 space-y-1"
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex w-full items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={cn("h-3 w-3", visual.iconColorClass)} />
                      <span
                        className={cn(
                          "font-medium text-sm",
                          !notification.isRead && "font-bold"
                        )}
                      >
                        {notification.title}
                      </span>
                    </div>
                    <span
                      className="text-xs text-muted-foreground whitespace-nowrap ml-2"
                      title={notification.readAt ? `Прочитано: ${new Date(notification.readAt).toLocaleString()}` : undefined}
                    >
                      {new Date(notification.sentAt).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  {notification.actorName && (
                    <p className="text-[10px] text-muted-foreground pl-5">
                      от {notification.actorName}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground line-clamp-2 pl-5">
                    {notification.message}
                  </p>
                  {!notification.isRead && (
                    <div className="flex justify-end w-full mt-1">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-[10px] px-2"
                        onClick={(e) => {
                          e.stopPropagation();
                          void markAsRead(notification.id);
                        }}
                      >
                        Прочитать
                      </Button>
                    </div>
                  )}
                </DropdownMenuItem>
                );
              })
            )}
          </ScrollArea>
        </DropdownMenuGroup>
        {unreadCount > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="cursor-pointer justify-center text-center"
              onClick={(e) => {
                e.preventDefault();
                void markAllAsRead();
              }}
            >
              <span className="text-sm font-medium text-primary">
                Отметить все как прочитанные
              </span>
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
